/**
 * Pure / setter-driven helpers extracted from usePendingChannelDrops to keep
 * the hook file under the 200-line FE limit.
 *
 * - `applyAiRefAdd`: AI-reference target → auto-replace (no slotInsert).
 * - `applyChannelAdd`: channel target → plan via slotInsert, stage pending +
 *   accumulate shifts when an existing occupant must move. Returns true when
 *   the drop was accepted (false on a no-op / reject so the caller can decide
 *   whether to also touch the reorderPlan).
 * - `deriveMaps`: derives the pending-by-channel/-ai-ref maps + affected sets
 *   from the flat pending map (memoised in the hook).
 */
import type { Dispatch, SetStateAction } from 'react'
import { slotInsert } from './slotInsert'
import { applyDelete, type AppliedDelete } from './applyDelete'
import type { ChannelSnapshot, PendingShift } from './buildReorderPlan'
import type { AddToProductImage, AddToProductTarget } from '../types'
import type { CurrentSlotsByChannel, PendingDrop } from './usePendingChannelDrops'

export function keyOf(t: AddToProductTarget): string {
  return t.type === 'channel' ? `c|${t.channelId}|${t.slot}` : `a||${t.slot}`
}

/** Merge a freshly-supplied snapshot into the persisted per-channel store. */
export function mergeSnapshot(
  prev: CurrentSlotsByChannel,
  next: CurrentSlotsByChannel,
): CurrentSlotsByChannel {
  const out = new Map(prev)
  for (const [ch, slots] of next.entries()) out.set(ch, slots)
  return out
}

/** Auto-replace path for AI-reference drops (no slotInsert). */
export function applyAiRefAdd(
  setPending: Dispatch<SetStateAction<Map<string, PendingDrop>>>,
  image: AddToProductImage,
  target: AddToProductTarget,
  tempKey: string,
): void {
  setPending((prev) => {
    const next = new Map(prev)
    next.set(keyOf(target), { image, target, tempKey })
    return next
  })
}

/**
 * Channel drop path: runs slotInsert and stages pending + shifts. Returns
 * `true` when the drop was accepted (so callers can also push to the
 * reorderPlan), `false` on a no-op (e.g. same-image-same-slot reject).
 */
export function applyChannelAdd(
  setPending: Dispatch<SetStateAction<Map<string, PendingDrop>>>,
  setShifts: Dispatch<SetStateAction<PendingShift[]>>,
  image: AddToProductImage,
  target: AddToProductTarget & { type: 'channel' },
  tempKey: string,
  currentSlotsByChannel?: CurrentSlotsByChannel,
): boolean {
  const channelSnap = currentSlotsByChannel?.get(target.channelId) ?? {}
  const plan = slotInsert(snapshotToSlotMap(channelSnap), target.slot, image)
  if (plan.kind === 'reject') return false
  setPending((prev) => {
    const next = new Map(prev)
    next.set(keyOf(target), { image, target, tempKey })
    return next
  })
  if (plan.kind === 'insert') {
    const newShifts: PendingShift[] = plan.shifted.map((s) => ({
      channelId: target.channelId,
      from: s.from,
      to: s.to,
      linkId: channelSnap[s.from]?.linkId,
    }))
    setShifts((prev) => [...prev, ...newShifts])
  }
  return true
}

function snapshotToSlotMap(snap: ChannelSnapshot): Record<number, AddToProductImage> {
  const out: Record<number, AddToProductImage> = {}
  for (const [slotStr, v] of Object.entries(snap)) out[Number(slotStr)] = v.image
  return out
}

/**
 * Drives a slot deletion: if the slot only holds a not-yet-persisted pending
 * drop, just pop it (no DB delete needed). Otherwise plan shifts via
 * applyDelete and push the persisted target into the deletes queue.
 */
export function applyDeleteRequest(
  setPending: Dispatch<SetStateAction<Map<string, PendingDrop>>>,
  setShifts: Dispatch<SetStateAction<PendingShift[]>>,
  setDeletes: Dispatch<SetStateAction<AppliedDelete[]>>,
  pending: Map<string, PendingDrop>,
  snapshot: CurrentSlotsByChannel,
  target: AddToProductTarget,
): void {
  const k = keyOf(target)
  if (pending.has(k)) {
    setPending((prev) => {
      const next = new Map(prev)
      next.delete(k)
      return next
    })
    return
  }
  const channelSnap =
    target.type === 'channel' ? (snapshot.get(target.channelId) ?? {}) : {}
  const applied = applyDelete(target, channelSnap)
  setDeletes((prev) => [...prev, applied])
  if (applied.shifts.length > 0) {
    setShifts((prev) => [...prev, ...applied.shifts])
  }
}

export function deriveMaps(pending: Map<string, PendingDrop>) {
  const pendingByChannelSlot = new Map<string, Map<number, AddToProductImage>>()
  const pendingByAiRefSlot = new Map<number, AddToProductImage>()
  const affectedChannels = new Set<string>()
  const affectedImages = new Set<string>()
  for (const { image, target } of pending.values()) {
    affectedImages.add(image.id)
    if (target.type === 'channel') {
      affectedChannels.add(target.channelId)
      let slotMap = pendingByChannelSlot.get(target.channelId)
      if (!slotMap) {
        slotMap = new Map()
        pendingByChannelSlot.set(target.channelId, slotMap)
      }
      slotMap.set(target.slot, image)
    } else {
      pendingByAiRefSlot.set(target.slot, image)
    }
  }
  return { pendingByChannelSlot, pendingByAiRefSlot, affectedChannels, affectedImages }
}
